import React, { useRef, useState, useEffect } from "react";
import { Box, Button } from "@mui/material";
import { UserNotification } from "../../models/notifications";
import { AssociativeArray } from "../../helpers/helpers";
import { ChannelKeys } from "./Notifications";


interface ChannelButtonsProps {
    notificationsData: UserNotification;
    activeButtonKey: ChannelKeys;
    buttonData: AssociativeArray;
    handleClick: (key: ChannelKeys) => void;
}

const ChannelButtons = ({ notificationsData, activeButtonKey: activeButton, buttonData, handleClick } : ChannelButtonsProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const el = scrollRef.current;
      if (!el) return;

      const { scrollWidth, clientWidth, scrollLeft } = el;
      setShowLeftFade(scrollLeft > 0);
      setShowRightFade(scrollLeft + clientWidth < scrollWidth);
    };

    const el = scrollRef.current;
    if (el) {
      checkOverflow();
      el.addEventListener("scroll", checkOverflow);
      window.addEventListener("resize", checkOverflow);
    }
    return () => {
      if (el) el.removeEventListener("scroll", checkOverflow);
      window.removeEventListener("resize", checkOverflow);
    };
  }, []);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", width: "100%" }}>
      <Box sx={{ position: "relative", width: "100%" }}>
        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            gap: "8px",
            marginBottom: "20px",
            overflowX: "auto",
            scrollbarWidth: "none", 
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {notificationsData?.channels?.sort((channel1, channel2) => channel1.id - channel2.id)
            .map((channel) => (
            <Button
              key={channel?.displayKey}
              startIcon={buttonData[channel?.displayKey]?.icon}
              variant="outlined"
              color={activeButton === channel?.displayKey ? "primary" : "greyed"}
              onClick={() => handleClick(channel.displayKey as ChannelKeys)}
              sx={{ flexShrink: 0 }}
            >
              {buttonData[channel?.displayKey]?.title}
            </Button>
          ))}
        </Box>

        {showLeftFade && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "40px",
              height: "100%",
              pointerEvents: "none",
              background: (theme) =>
                `linear-gradient(to right, ${theme.palette.background.default} 60%, transparent)`,
            }}
          />
        )}

        {showRightFade && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "40px",
              height: "100%",
              pointerEvents: "none",
              background: (theme) =>
                `linear-gradient(to left, ${theme.palette.background.default} 60%, transparent)`,
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export default ChannelButtons;
